import { cloneElement, useState } from 'react'
import { LabelCapped } from 'components/Commons/Inputs/Template'
import Checked from './assets/checked.svg?react'
import Unchecked from './assets/unchecked.svg?react'
import PartialChecked from './assets/partialChecked.svg?react'
import Add from './assets/add.svg?react'
import AddHover from './assets/addHover.svg?react'

import {
  Template,
  Container,
  Wrapper,
  IconCheckbox,
  IconRadio,
  IconRadioWithCheck,
  Element,
  RadioChecked,
} from './styled'

function useHoverItem({ disabled }) {
  const [isHovered, setIsHovered] = useState(false)

  const onMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true)
    }
  }
  const onMouseLeave = () => {
    if (!disabled) {
      setIsHovered(false)
    }
  }
  return { isHovered, onMouseEnter, onMouseLeave }
}

function getItemsSelected(
  value,
  checked,
  selectedItems,
  isNullState = false,
  partialSelectedItems = [],
  initialPartialSelected = [],
) {
  if (isNullState) {
    // If isNullState, remove it from the partialSelectedItems and add them to the selected items
    return {
      selected: [...selectedItems, value],
      partialSelected: partialSelectedItems.filter(v => v !== value),
    }
  } else if (checked) {
    // If checked, remove it from the selected items
    return {
      selected: selectedItems.filter(v => v !== value),
      partialSelected: partialSelectedItems,
    }
  } else if (initialPartialSelected.includes(value)) {
    // If the item is not selected, check if it is in the initialPartialSelected array and if so, remove it to the partial selected
    return {
      selected: selectedItems,
      partialSelected: [...partialSelectedItems, value],
    }
  }
  // If the item is not selected and is not in the initialPartialSelected array, add it to the selected items
  return {
    selected: [...selectedItems, value],
    partialSelected: partialSelectedItems,
  }
}

function getIconComponent(isNullState, checked, isNewItem, isHovered) {
  switch (true) {
    case isNullState:
      return PartialChecked
    case checked:
      return Checked
    case isNewItem && isHovered:
      return AddHover
    case isNewItem:
      return Add
    default:
      return Unchecked
  }
}

export function IconRadioButton({ checked }) {
  return <IconRadio checked={checked} />
}

export function IconRadioButtonWithCheck({ checked, onClick }) {
  return (
    <IconRadioWithCheck checked={checked} onClick={onClick}>
      {checked && <RadioChecked component={Checked} />}
    </IconRadioWithCheck>
  )
}

export function IconCheckboxButton({ checked, iconSize, color = 'primary', isNullState, isNewItem, isHovered }) {
  const Component = getIconComponent(isNullState, checked, isNewItem, isHovered)
  return <IconCheckbox component={Component} color={checked ? color : 'text'} iconSize={iconSize} />
}

function Checkbox({
  dataTestId,
  className,
  element,
  checked: isChecked,
  onChange,
  color,
  reverse,
  scalable,
  disabled,
  iconSize,
  isNullState,
  isNewItem,
  Icon = <IconCheckboxButton />,
}) {
  const checked = !isNullState && isChecked
  const { isHovered, ...hoverUtils } = useHoverItem({ disabled })

  function handleClick(e) {
    e.preventDefault()
    if (!disabled && onChange) {
      onChange(!checked)
    }
  }

  return (
    <Container
      data-testid={dataTestId}
      className={className}
      onClick={handleClick}
      reverse={reverse}
      scalable={scalable}
      disabled={disabled}
      {...hoverUtils}
    >
      <Wrapper>{cloneElement(Icon, { checked, color, iconSize, isNullState, isNewItem, isHovered })}</Wrapper>
      {element && <Element reverse={reverse}>{element}</Element>}
    </Container>
  )
}

export function InternalCheckBox({
  className,
  label,
  disabled: globalDisabled,
  selected = [],
  partialSelected = [],
  onChange,
  items,
  error,
  isRequired,
  Icon,
  iconSize,
  conditionChecked,
  reverse = false,
  scalable = false,
  layoutConfiguration,
  bottomElement,
}) {
  return (
    <Template
      label={label}
      error={error}
      Wrapper={LabelCapped}
      layoutConfiguration={layoutConfiguration}
      isRequired={isRequired}
      disabled={globalDisabled}
      className={className}
    >
      {items.map(item => {
        const { value, isNewItem } = item
        const checked = conditionChecked(selected, value)
        const isNullState = conditionChecked(partialSelected, value)

        const itemDisabled = item.disabled

        return (
          <>
            <Checkbox
              key={value}
              element={item.element}
              onChange={() => onChange(value, checked, selected, isNullState, partialSelected)}
              checked={checked}
              Icon={Icon}
              reverse={reverse}
              scalable={scalable}
              disabled={globalDisabled || itemDisabled}
              isNullState={isNullState}
              iconSize={iconSize}
              isNewItem={isNewItem}
              dataTestId={`checkbox_${value}`}
            />
            {bottomElement && cloneElement(bottomElement, { item, checked })}
          </>
        )
      })}
    </Template>
  )
}

export function CheckboxMultiple({
  onChange,
  CheckboxComponent = InternalCheckBox,
  iconSize,
  initialPartialSelected = [],
  Icon = <IconCheckboxButton iconSize={iconSize} />,
  bottomElement,
  ...props
}) {
  function onToggle(value, checked, selectedItems, isNullState, partialSelectedItems) {
    const { selected, partialSelected } = getItemsSelected(
      value,
      checked,
      selectedItems,
      isNullState,
      partialSelectedItems,
      initialPartialSelected,
    )
    onChange(selected, partialSelected, value)
  }

  function conditionChecked(selected, value) {
    return selected.includes(value)
  }

  return (
    <CheckboxComponent
      onChange={onToggle}
      Icon={Icon}
      iconSize={iconSize}
      conditionChecked={conditionChecked}
      bottomElement={bottomElement}
      {...props}
    />
  )
}

export function RadioButton({ onChange, RadioComponent = InternalCheckBox, ...props }) {
  function onToggle(value) {
    onChange(value)
  }

  function conditionChecked(selected, value) {
    return selected === value
  }
  return (
    <RadioComponent onChange={onToggle} {...props} Icon={<IconRadioButton />} conditionChecked={conditionChecked} />
  )
}

export default Checkbox
